import { useQuery } from '@apollo/client';
import { countryToRelavanceWord } from '@application/types';
import { useRouter } from '@application/utils';
import { graphql } from '@gql/gql';
import type {
  ApplicationGetSuggestedBreedersQuery,
  ApplicationGetSuggestedBreedersQueryVariables,
} from '@gql/graphql';
import { CountryCode, DogBreederOrderByRelevanceFieldEnum, SortOrder } from '@gql/graphql';
import dayjs from 'dayjs';
import { useMemo } from 'react';

export const SUGGESTED_BREEDERS_FOR_APPLICATIONS_LIMIT = 3;

export const APPLICATION_GET_SUGGESTED_BREEDERS = graphql(`
  query ApplicationGetSuggestedBreeders(
    $where: DogBreederWhereInput
    $orderBy: [DogBreederOrderByWithRelationAndSearchRelevanceInput!]
    $take: Int
    $breederCardInfoTranslationsWhere: DogBreederTranslationWhereInput
    $breederCardInfoTranslationsWhereBreed: DogBreedTranslationWhereInput
    $breederCardInfoBreedImgsTake: Int
    $breederCardInfoLittersWhere: LitterWhereInput
  ) {
    findManyDogBreeder(where: $where, orderBy: $orderBy, take: $take) {
      ...BreederCardInfo
    }
  }
`);

const sixMonthsAgo = dayjs().subtract(6, 'months').toISOString();

export const useGetSuggestedBreeders = (breederIds: number[], breedIds: number[]) => {
  const { language, countryCode } = useRouter();

  const orderBySearchRelevance = useMemo(() => {
    if (countryCode === CountryCode.EU) {
      return '"has <-> litters"';
    }
    return `${countryToRelavanceWord[countryCode]} | "has <-> litters"`;
  }, [countryCode]);

  const variables = useMemo((): ApplicationGetSuggestedBreedersQueryVariables => {
    return {
      take: 12,
      where: {
        id: { notIn: breederIds },
        breederBreeds: { some: { dogBreedId: { in: breedIds } } },
        certified: { equals: true },
        public: { equals: true },
        account: {
          is: {
            connectedAt: {
              gte: sixMonthsAgo,
            },
          },
        },
      },
      orderBy: [
        {
          _relevance: {
            fields: [DogBreederOrderByRelevanceFieldEnum.tags],
            search: orderBySearchRelevance,
            sort: SortOrder.desc,
          },
        },
        { account: { connectedAt: SortOrder.desc } },
      ],
      breederCardInfoTranslationsWhere: { language: { equals: language } },
      breederCardInfoTranslationsWhereBreed: { language: { equals: language } },
      breederCardInfoBreedImgsTake: 1,
      breederCardInfoLittersWhere: {
        archived: { equals: false },
        adminValidated: { equals: true },
      },
    };
  }, [breederIds, breedIds, orderBySearchRelevance, language]);

  const { data, error, loading } = useQuery<
    ApplicationGetSuggestedBreedersQuery,
    ApplicationGetSuggestedBreedersQueryVariables
  >(APPLICATION_GET_SUGGESTED_BREEDERS, {
    variables,
    skip: breederIds.length === 0 || breedIds.length === 0,
  });

  return {
    breeders: data?.findManyDogBreeder ?? [],
    breedersError: error,
    breedersLoading: loading,
    breedersVariables: variables,
  };
};
